import {
  AppBox,
  AppButton,
  AppDatePicker,
  AppGridBox,
  AppInput,
  AppSelect,
  AppText,
} from "../../../../../commons/components";
import { Controller, useForm } from "react-hook-form";
import { findInputError, isFormInvalid } from "../../../../../commons/utilities/form-utils";
import moment from "moment";
import { FieldErrors, FieldValues } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { setStepperFormData } from "../../../../../redux/slices/stepper-form-slice";
import { formatDate } from "../../../../../commons/utilities/date-utils";
import { ISelectOptions } from "../../../../../commons/components/ui-components/Select";
import { CountryCodeEnum } from "../../../../../commons/enums/country-code-enum";
import { useEffect, useLayoutEffect, useState } from "react";
import { findIndexByCondition } from "../../../../../commons/utilities/array-utils";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import { toSentenceCase } from "../../../../../commons/utilities/string-utils";
import { IFootballOrganizer } from "../../../../../commons/models/football/interface/i-football-organizer";
import { IFootballOrganizerDTO } from "../../../../../api-services/football/admin/manage-football-organizer-service";
import { countryOptions } from "../../../../../commons/utilities/country-utils";

interface OrganizerBasicInfoAddEditProps {
  footballOrganizerData?: IFootballOrganizer | null;
  formStep: number;
  formErrors: FieldErrors<FieldValues>;
  submitStatus: StatusEnum;
  onBack: (currentStep: number) => void;
  onSubmit: (currentStep: number) => void;
  onClearError: (errorKey: string) => void;
}

const OrganizerBasicInfoAddEdit = ({
  footballOrganizerData,
  formStep,
  formErrors,
  submitStatus,
  onBack,
  onSubmit,
  onClearError,
}: OrganizerBasicInfoAddEditProps) => {
  const methods = useForm();
  const dispatch = useAppDispatch();
  const formData = useAppSelector<IFootballOrganizerDTO>((state) => state.stepperForm.stepperFormData);
  const [countryIndex, setCountryIndex] = useState<number>(0);

  useEffect(() => {
    if (!footballOrganizerData && countryOptions) {
      const footballOrganizerData: IFootballOrganizerDTO = {
        ...formData,
        country: countryOptions[0].value.toString(),
      };
      dispatch(setStepperFormData<IFootballOrganizerDTO>(footballOrganizerData));
    }
  }, []);

  useLayoutEffect(() => {
    if (footballOrganizerData) {
      let countryIndex = findIndexByCondition(
        countryOptions,
        (option) => option.value === footballOrganizerData.country
      );
      if (countryIndex >= 0) {
        setCountryIndex(countryIndex);
      } else {
        setCountryIndex(0);
      }
      methods.setValue("country", `${footballOrganizerData.country}`);
    }
  }, [footballOrganizerData]);

  const convertValue = (key: string, value: string): any => {
    const numericKeys = ["country"];
    return numericKeys.includes(key) ? value : value;
  };

  const handleInputChange = (value: string, key: string) => {
    const footballOrganizerData: IFootballOrganizerDTO = {
      ...formData,
      [key]: value,
    };
    dispatch(setStepperFormData<IFootballOrganizerDTO>(footballOrganizerData));
    onClearError(key);
  };
  return (
    <AppBox flexDirection="column" justifyContent="space-between" gap="sm" className="flex-1">
      <AppBox flexDirection="column" gap="sm" className="w-100">
        <Controller
          name="name"
          defaultValue={footballOrganizerData ? footballOrganizerData.name : ""}
          control={methods.control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <AppBox flexDirection="column">
              <AppInput
                id="name"
                label="Name *"
                placeholder="Enter organizer's name"
                type="text"
                defaultValue={value}
                onChange={(event) => {
                  onChange(event);
                  handleInputChange(event.target.value, "name");
                }}
                flex="row"
              />
              <AppGridBox style={{ gridTemplateColumns: "2fr 1fr", direction: "rtl" }} gap="sm">
                {isFormInvalid(findInputError(formErrors, "name")) && (
                  <AppText as="span" color="danger" textAlign="end">
                    <>{toSentenceCase(`${formErrors.name?.message ?? "Venue name is required."}`)}</>
                  </AppText>
                )}
              </AppGridBox>
            </AppBox>
          )}
        />
        <Controller
          name="email"
          defaultValue={footballOrganizerData ? footballOrganizerData.email : ""}
          control={methods.control}
          rules={{ required: false }}
          render={({ field: { onChange, value } }) => (
            <AppBox flexDirection="column">
              <AppInput
                id="email"
                label="Contact email"
                placeholder="Enter organizer's contact email"
                type="text"
                defaultValue={value}
                flex="row"
                onChange={(event) => {
                  onChange(event);
                  handleInputChange(event.target.value, "email");
                }}
              />
              <AppGridBox style={{ gridTemplateColumns: "2fr 1fr", direction: "rtl" }} gap="sm">
                {isFormInvalid(findInputError(formErrors, "email")) && (
                  <AppText as="span" color="danger" textAlign="end">
                    <>{toSentenceCase(`${formErrors.email?.message}`)}</>
                  </AppText>
                )}
              </AppGridBox>
            </AppBox>
          )}
        />
        <Controller
          name="website"
          defaultValue={footballOrganizerData ? footballOrganizerData.website : ""}
          control={methods.control}
          rules={{ required: false }}
          render={({ field: { onChange, value } }) => (
            <AppBox flexDirection="column">
              <AppInput
                id="website"
                label="Website"
                placeholder="Enter organizer's website"
                type="text"
                defaultValue={value}
                flex="row"
                onChange={(event) => {
                  onChange(event);
                  handleInputChange(event.target.value, "website");
                }}
              />
              <AppGridBox style={{ gridTemplateColumns: "2fr 1fr", direction: "rtl" }} gap="sm">
                {isFormInvalid(findInputError(formErrors, "website")) && (
                  <AppText as="span" color="danger" textAlign="end">
                    <>{toSentenceCase(`${formErrors.website?.message}`)}</>
                  </AppText>
                )}
              </AppGridBox>
            </AppBox>
          )}
        />
        <Controller
          name="country"
          defaultValue={footballOrganizerData ? footballOrganizerData.country : ""}
          control={methods.control}
          rules={{ required: false }}
          render={({ field: { onChange } }) => (
            <AppBox flexDirection="column" gap="2xs">
              <AppGridBox className="w-100 flex-1" style={{ gridTemplateColumns: "1fr 2fr" }} gap="sm">
                <AppBox alignItems="center">
                  <AppText as="label" size="lg">
                    Country
                  </AppText>
                </AppBox>
                <AppSelect
                  options={countryOptions}
                  onChange={(option) => {
                    onChange(option.value);
                    setCountryIndex(
                      findIndexByCondition(countryOptions, (countryOption) => countryOption.value === option.value)
                    );
                    handleInputChange(option.value.toString(), "country");
                  }}
                  currentOption={countryOptions[countryIndex ?? 0]}
                />
              </AppGridBox>
              <AppGridBox style={{ gridTemplateColumns: "2fr 1fr", direction: "rtl" }} gap="sm">
                {isFormInvalid(findInputError(formErrors, "country")) && (
                  <AppText as="span" color="danger" textAlign="end">
                    <>{toSentenceCase(`${formErrors.country?.message}`)}</>
                  </AppText>
                )}
              </AppGridBox>
            </AppBox>
          )}
        />
      </AppBox>
      <AppBox gap="xs" justifyContent="end">
        <AppButton
          label="Back"
          variant="outline"
          color="gray"
          borderLight
          onClick={() => {
            onBack(formStep);
          }}
          disabled={submitStatus === StatusEnum.Loading}
        />
        <AppButton
          type="submit"
          label={footballOrganizerData ? "Save Changes" : "Create"}
          onClick={() => {
            onSubmit(formStep);
          }}
          loading={submitStatus === StatusEnum.Loading}
        />
      </AppBox>
    </AppBox>
  );
};

export default OrganizerBasicInfoAddEdit;
